import { z } from 'zod';
import {
  ApplyOutcomeSchema,
  ConfigObjectSchema,
  FieldChangeSchema,
  MaterialEntrySchema,
  RedactedFieldSchema,
  ResourceSummarySchema,
  SchemaDescriptorSchema,
  ValidateOutcomeSchema,
} from './schema.js';

/**
 * HTTP API v1 between the web app and the backend. All routes live under API_BASE_PATH, exchange JSON and
 * authenticate with the session cookie. Mutating requests (POST, PATCH, DELETE) must send the session's CSRF
 * token in the CSRF_HEADER header, except /auth/register and /auth/login. See docs/API_CONTRACT.md.
 */

export const API_BASE_PATH = '/api/v1';
export const SESSION_COOKIE = 'comficonf_session';
export const CSRF_HEADER = 'x-csrf-token';

export const API_ERROR_CODES = [
  'VALIDATION_FAILED',
  'UNAUTHENTICATED',
  'CSRF_FAILED',
  'FORBIDDEN',
  'NOT_FOUND',
  'EMAIL_TAKEN',
  'REGISTRATION_DISABLED',
  'INVALID_CREDENTIALS',
  'INVALID_PAIRING_CODE',
  'RATE_LIMITED',
  'PAYLOAD_TOO_LARGE',
  'SERVER_OFFLINE',
  'SERVER_DISCONNECTED',
  'BRIDGE_TIMEOUT',
  'BRIDGE_ERROR',
  'UNKNOWN_RESOURCE',
  'OPERATION_IN_PROGRESS',
  'OPERATION_ID_REUSED',
  'OPERATION_OUTCOME_LOST',
  'INTERNAL',
] as const;
export const ApiErrorCodeSchema = z.enum(API_ERROR_CODES);
export type ApiErrorCode = z.infer<typeof ApiErrorCodeSchema>;

/** Body of every non-2xx response. */
export const ApiErrorBodySchema = z.object({
  error: z.object({
    code: ApiErrorCodeSchema,
    message: z.string(),
    /**
     * VALIDATION_FAILED: { path, message }[]; BRIDGE_ERROR: { bridgeCode, outcome?: 'unknown' };
     * SERVER_DISCONNECTED, BRIDGE_TIMEOUT and OPERATION_OUTCOME_LOST: { outcome: 'unknown' }.
     */
    details: z.unknown().optional(),
  }),
});
export type ApiErrorBody = z.infer<typeof ApiErrorBodySchema>;

// ---- Auth ------------------------------------------------------------------------------------

export const EmailSchema = z.email().max(320);
export const PasswordSchema = z.string().min(10).max(256);

export const UserSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  createdAt: z.iso.datetime(),
});
export type User = z.infer<typeof UserSchema>;

/** POST /auth/register */
export const RegisterRequestSchema = z.object({
  email: EmailSchema,
  password: PasswordSchema,
});
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;

/** POST /auth/login */
export const LoginRequestSchema = z.object({
  email: z.string().min(1).max(320),
  password: z.string().min(1).max(256),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

/** Response of /auth/register (201), /auth/login (200) and GET /auth/session (200). */
export const SessionResponseSchema = z.object({
  user: UserSchema,
  csrfToken: z.string().min(1),
});
export type SessionResponse = z.infer<typeof SessionResponseSchema>;

/** POST /auth/password (204). Revokes the user's other sessions. */
export const ChangePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1).max(256),
  newPassword: PasswordSchema,
});
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequestSchema>;

// ---- Servers ---------------------------------------------------------------------------------

export const ServerStatusSchema = z.enum(['online', 'offline']);
export type ServerStatus = z.infer<typeof ServerStatusSchema>;

export const ServerSummarySchema = z.object({
  id: z.uuid(),
  displayName: z.string().min(1).max(64),
  status: ServerStatusSchema,
  lastSeenAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  platform: z.string().nullable(),
  platformVersion: z.string().nullable(),
  minecraftVersion: z.string().nullable(),
  bridgeVersion: z.string().nullable(),
  /** Number of plugins with registered resources, from the live connection; null while offline. */
  pluginCount: z.int().nullable(),
  /** Number of registered resources, from the live connection; null while offline. */
  resourceCount: z.int().nullable(),
});
export type ServerSummary = z.infer<typeof ServerSummarySchema>;

export const ServerDetailSchema = ServerSummarySchema.extend({
  installation: z.object({
    id: z.uuid(),
    protocolVersion: z.int(),
    pairedAt: z.iso.datetime(),
  }),
});
export type ServerDetail = z.infer<typeof ServerDetailSchema>;

/** GET /servers */
export const ServersResponseSchema = z.object({ servers: z.array(ServerSummarySchema) });
export type ServersResponse = z.infer<typeof ServersResponseSchema>;

/** GET /servers/:serverId, PATCH /servers/:serverId, POST /pairing/claim (201) */
export const ServerResponseSchema = z.object({ server: ServerDetailSchema });
export type ServerResponse = z.infer<typeof ServerResponseSchema>;

export const ServerDisplayNameSchema = z.string().trim().min(1).max(64);

/** PATCH /servers/:serverId */
export const UpdateServerRequestSchema = z.object({ displayName: ServerDisplayNameSchema });
export type UpdateServerRequest = z.infer<typeof UpdateServerRequestSchema>;

/**
 * POST /pairing/claim. The code is case-insensitive; spaces and a missing dash are tolerated. Any code that does not
 * normalize to 8 valid symbols is answered with INVALID_PAIRING_CODE.
 */
export const ClaimPairingRequestSchema = z.object({
  code: z.string().max(16),
  displayName: ServerDisplayNameSchema.optional(),
});
export type ClaimPairingRequest = z.infer<typeof ClaimPairingRequestSchema>;

// ---- Plugins and resources --------------------------------------------------------------------

export const PluginResourceSchema = ResourceSummarySchema.omit({ plugin: true });
export type PluginResource = z.infer<typeof PluginResourceSchema>;

export const PluginSummarySchema = z.object({
  slug: z.string(),
  name: z.string(),
  version: z.string(),
  resources: z.array(PluginResourceSchema),
});
export type PluginSummary = z.infer<typeof PluginSummarySchema>;

/** GET /servers/:serverId/plugins */
export const PluginsResponseSchema = z.object({ plugins: z.array(PluginSummarySchema) });
export type PluginsResponse = z.infer<typeof PluginsResponseSchema>;

/** GET /servers/:serverId/resources/:resourceId (resourceId URL-encoded) */
export const ResourceResponseSchema = z.object({
  resource: ResourceSummarySchema,
  schema: SchemaDescriptorSchema,
  snapshot: z.object({
    revision: z.string(),
    values: ConfigObjectSchema,
    redacted: z.array(RedactedFieldSchema),
  }),
});
export type ResourceResponse = z.infer<typeof ResourceResponseSchema>;

/** POST /servers/:serverId/resources/:resourceId/validate */
export const ValidateRequestSchema = z.object({
  baseRevision: z.string().min(1).max(128),
  values: ConfigObjectSchema,
});
export type ValidateRequest = z.infer<typeof ValidateRequestSchema>;
export const ValidateResponseSchema = ValidateOutcomeSchema;
export type ValidateResponse = z.infer<typeof ValidateResponseSchema>;

/**
 * POST /servers/:serverId/resources/:resourceId/apply. Every plugin outcome (APPLIED, SAVED_RESTART_REQUIRED,
 * REJECTED, CONFLICT, FAILED) is HTTP 200 with the outcome in `result`; transport problems use error codes.
 * Exception: a CONFLICT with `duplicate: false` answered to an operation that is forwarded again after an unknown
 * outcome is 409 OPERATION_OUTCOME_LOST, because the earlier attempt may have applied the change.
 */
export const ApplyRequestSchema = z.object({
  /** Generated by the client once per Apply click; resubmitting the same id never applies twice. */
  operationId: z.uuid(),
  baseRevision: z.string().min(1).max(128),
  values: ConfigObjectSchema,
});
export type ApplyRequest = z.infer<typeof ApplyRequestSchema>;
export const ApplyResponseSchema = z.object({ result: ApplyOutcomeSchema });
export type ApplyResponse = z.infer<typeof ApplyResponseSchema>;

/** GET /servers/:serverId/catalogs/materials */
export const MaterialsResponseSchema = z.object({
  minecraftVersion: z.string(),
  entries: z.array(MaterialEntrySchema),
});
export type MaterialsResponse = z.infer<typeof MaterialsResponseSchema>;

// ---- Audit -----------------------------------------------------------------------------------

export const AUDIT_OPERATIONS = [
  'server.paired',
  'server.renamed',
  'server.revoked',
  'config.applied',
] as const;
export const AuditOperationSchema = z.enum(AUDIT_OPERATIONS);
export type AuditOperation = z.infer<typeof AuditOperationSchema>;

export const AuditEventSchema = z.object({
  id: z.uuid(),
  createdAt: z.iso.datetime(),
  serverId: z.uuid(),
  actor: z.object({ id: z.uuid(), email: z.string() }).nullable(),
  operation: AuditOperationSchema,
  /** config.applied: the ApplyStatus, or PENDING / UNKNOWN; other operations: OK. */
  result: z.string(),
  pluginSlug: z.string().nullable(),
  resourceId: z.string().nullable(),
  /**
   * Safe metadata only. config.applied: { operationId, changeCount, changes: FieldChange[] with values
   * truncated to 200 characters and sensitive values omitted, message? }.
   */
  metadata: z.object({
    operationId: z.uuid().optional(),
    changeCount: z.int().optional(),
    changes: z.array(FieldChangeSchema).optional(),
    message: z.string().optional(),
    displayName: z.string().optional(),
  }),
});
export type AuditEvent = z.infer<typeof AuditEventSchema>;

/** GET /servers/:serverId/audit?limit=50&before=<cursor> */
export const AuditResponseSchema = z.object({
  events: z.array(AuditEventSchema),
  nextCursor: z.string().nullable(),
});
export type AuditResponse = z.infer<typeof AuditResponseSchema>;

// ---- Public statistics -----------------------------------------------------------------------

/**
 * GET /public/stats. Aggregate counts for the website; no session, any Origin. The backend caches the counts for
 * up to 60 s, so they lag behind the live state by at most that long.
 */
export const PublicStatsResponseSchema = z.object({
  /** Servers with an authenticated, paired bridge connection. */
  connectedServers: z.int().min(0),
  /** Paired servers that are not deleted. */
  pairedServers: z.int().min(0),
  /** config.applied audit rows with result APPLIED (the plugin confirmed runtime activation), all time. */
  changesApplied: z.int().min(0),
});
export type PublicStatsResponse = z.infer<typeof PublicStatsResponseSchema>;

// ---- Server-sent events (GET /events) ---------------------------------------------------------

/** `event:` names and `data:` payloads of the GET /events stream. */
export const ServerStatusEventSchema = z.object({
  serverId: z.uuid(),
  status: ServerStatusSchema,
});
export const ServerResourcesEventSchema = z.object({
  serverId: z.uuid(),
  /** Resource ids whose values or schema changed outside ComfiConf; empty when only the list changed. */
  changed: z.array(z.string()),
});
export const ServerRemovedEventSchema = z.object({ serverId: z.uuid() });

export const SSE_EVENTS = {
  'server.status': ServerStatusEventSchema,
  'server.resources': ServerResourcesEventSchema,
  'server.removed': ServerRemovedEventSchema,
} as const;
export type SseEventName = keyof typeof SSE_EVENTS;
